// Despacho del día: arma la carga por ruta (porciones por cliente y servicio),
// registra el checklist de salida (temperatura + contenedores, bloqueante) y
// emite la guía de remisión SRI (codDoc 06) a los clientes de la ruta.
// Catering → Logística → Despacho del día.
import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Truck, Thermometer, FileText, PlusCircle, PackageCheck, Send, MapPin, CheckCircle2, AlertTriangle, QrCode, Copy } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { getErrorMessage } from '@/lib/api/client';
import { useDialogoEnLinea } from '@/components/ui/PanelIntegrado';
import { fieldCls, num } from '@/features/inventario/shared';
import { useTabsStore } from '@/stores/tabs.store';
import { catRpc, invalidateCatering, SERVICIO_LABEL } from './shared';
import { MenajeRetorno } from './MenajeRetorno';
import { LOCALE } from '@/lib/utils/regional';

interface EntregaRow {
  contrato_id: string; cliente: string; orden: number; raciones: number; estado: string;
  hora_real: string | null; temp_llegada_c: number | null; receptor_nombre: string | null;
  tiene_firma: boolean; tiene_foto: boolean; observaciones: string | null; incidencia_motivo: string | null; origen: string | null;
  firma_url: string | null; foto_url: string | null; media_estado: string | null;
}

interface Pendiente { ruta_id: string; ruta: string; paradas: number; pax: number }
interface DespachoRow {
  id: string; codigo: string; fecha: string; estado: string; ruta: string; placa: string;
  paradas: number; porciones: number; temp_salida_c: number | null; guia_estado: string | null;
}
interface LineaDet { id: string; servicio: string; porciones: number; contenedores: number; menaje: string | null }
interface ParadaDet { parada_id: string | null; contrato_id: string; contrato: string; cliente: string; orden: number; hora_objetivo: string | null; direccion: string | null; lineas: LineaDet[] }
interface GuiaDet { id: string; estado_emision: string; serie: string; clave_acceso: string | null; mensaje_sri: string | null; ride_url: string | null }
interface DespachoDet {
  id: string; codigo: string; fecha: string; estado: string; ruta_id: string; ruta: string; vehiculo: string | null;
  placa: string | null; chofer: string | null; temp_salida_c: number | null; hora_salida: string | null;
  observaciones: string | null; total_porciones: number; total_contenedores: number;
  guia: GuiaDet | null; paradas: ParadaDet[];
}

const ESTADO_CLS: Record<string, string> = {
  borrador: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300',
  cargado: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300',
  en_ruta: 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300',
  cerrado: 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300',
  anulado: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300',
};
const GUIA_CLS: Record<string, string> = {
  autorizado: 'text-green-700 dark:text-green-400', encolado: 'text-amber-600', procesando: 'text-amber-600',
  error: 'text-red-600', devuelto: 'text-red-600', rechazado: 'text-red-600', por_emitir: 'text-muted-foreground',
};
const isoHoy = (): string => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; };

export function DespachoCatering() {
  const qc = useQueryClient();
  const dlg = useDialogoEnLinea();
  const openTab = useTabsStore((s) => s.openTab);
  const [fecha, setFecha] = useState(isoHoy());
  const [selId, setSelId] = useState('');
  const [temp, setTemp] = useState('');
  const [conts, setConts] = useState<Record<string, string>>({});
  const [menajes, setMenajes] = useState<Record<string, string>>({});

  const invalidar = () => invalidateCatering(qc);

  const { data: pendientes = [] } = useQuery<Pendiente[]>({
    queryKey: ['cat-desp-pendientes', fecha], queryFn: () => catRpc<Pendiente[]>('cat_despachos_pendientes', { _fecha: fecha }),
  });
  const { data: despachos = [] } = useQuery<DespachoRow[]>({
    queryKey: ['cat-desp-listar', fecha], queryFn: () => catRpc<DespachoRow[]>('cat_despachos_listar', { _fecha: fecha }),
  });
  const { data: det } = useQuery<DespachoDet>({
    queryKey: ['cat-desp-obtener', selId], enabled: !!selId, queryFn: () => catRpc<DespachoDet>('cat_despacho_obtener', { _id: selId }),
  });
  const enReparto = !!det && ['cargado', 'en_ruta', 'cerrado'].includes(det.estado);
  const { data: entregas = [] } = useQuery<EntregaRow[]>({
    queryKey: ['cat-desp-entregas', selId], enabled: enReparto,
    queryFn: () => catRpc<EntregaRow[]>('cat_despacho_entregas', { _despacho_id: selId }), refetchInterval: 20000,
  });
  const [repartoUrl, setRepartoUrl] = useState('');
  const genToken = useMutation({
    mutationFn: (ruta_id: string) => catRpc<{ ruta_publica: string }>('cat_reparto_token', { _ruta_id: ruta_id }),
    onSuccess: (r) => setRepartoUrl(`${window.location.origin}${import.meta.env.BASE_URL.replace(/\/$/, '')}${r.ruta_publica}`),
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const registrarEntrega = useMutation({
    mutationFn: (v: { contrato_id: string; estado: string }) => catRpc('cat_entrega_registrar', {
      _despacho_id: selId, _contrato_id: v.contrato_id, _payload: { estado: v.estado },
    }),
    onSuccess: () => { toast.success('Entrega registrada'); invalidar(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const generar = useMutation({
    mutationFn: (ruta_id: string) => catRpc<{ despacho_id: string }>('cat_despacho_generar', { _ruta_id: ruta_id, _fecha: fecha }),
    onSuccess: (r) => { toast.success('Despacho generado'); setSelId(r.despacho_id); invalidar(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const cargar = useMutation({
    mutationFn: () => catRpc('cat_despacho_cargar', {
      _id: selId, _temp_salida_c: temp === '' ? null : parseFloat(temp),
      _lineas: Object.entries(conts).map(([id, c]) => ({ id, contenedores: parseInt(c || '0', 10), menaje: menajes[id] || '' })),
    }),
    onSuccess: () => { toast.success('Carga registrada'); invalidar(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const emitirGuia = useMutation({
    mutationFn: () => catRpc<{ estado: string }>('cat_despacho_emitir_guia', { _id: selId, _datos: {} }),
    onSuccess: () => { toast.success('Guía de remisión encolada al SRI'); invalidar(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const estado = useMutation({
    mutationFn: (e: string) => catRpc('cat_despacho_estado', { _id: selId, _estado: e }),
    onSuccess: (_d, e) => { toast.success(e === 'anulado' ? 'Despacho anulado' : `Despacho ${e.replace('_', ' ')}`); invalidar(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const totalPax = useMemo(() => pendientes.reduce((s, p) => s + Number(p.pax || 0), 0), [pendientes]);
  // sugerencia de contenedores: 1 por cada 40 porciones
  const sugerir = (porciones: number) => String(Math.max(1, Math.ceil(porciones / 40)));

  return (
    <div className="space-y-4 p-4">
      {dlg.ui}
      <div className="flex flex-wrap items-end gap-3">
        <div><label className="block text-xs text-muted-foreground">Fecha de despacho</label>
          <input type="date" className={`${fieldCls} w-44`} value={fecha} onChange={(e) => { setFecha(e.target.value); setSelId(''); }} /></div>
      </div>

      {/* Rutas sin despachar */}
      {pendientes.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-base"><Truck className="h-4 w-4" /> Rutas por despachar</CardTitle>
            <p className="pt-1 text-xs text-muted-foreground">{pendientes.length} ruta(s) con pax del día sin despacho · {num(totalPax, 0)} raciones en total</p>
          </CardHeader>
          <CardContent className="flex flex-wrap gap-2">
            {pendientes.map((p) => (
              <div key={p.ruta_id} className="flex items-center gap-3 rounded-md border px-3 py-2 text-sm">
                <div><span className="font-medium">{p.ruta}</span><span className="block text-xs text-muted-foreground">{p.paradas} parada(s) · {num(p.pax, 0)} raciones</span></div>
                <Button size="sm" disabled={generar.isPending} onClick={() => generar.mutate(p.ruta_id)}><PlusCircle className="mr-1 h-3.5 w-3.5" /> Despachar</Button>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-5">
        {/* Despachos del día */}
        <Card className="lg:col-span-2">
          <CardHeader className="pb-2"><CardTitle className="text-base">Despachos del {fecha}</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            {despachos.map((d) => (
              <button key={d.id} onClick={() => setSelId(d.id)}
                className={`w-full rounded-md border px-3 py-2 text-left text-sm hover:bg-accent ${selId === d.id ? 'border-primary bg-accent' : ''}`}>
                <span className="flex items-center justify-between">
                  <span className="font-medium">{d.codigo} · {d.ruta}</span>
                  <span className={`rounded-full px-2 py-0.5 text-xs ${ESTADO_CLS[d.estado]}`}>{d.estado.replace('_', ' ')}</span>
                </span>
                <span className="flex items-center justify-between pt-0.5 text-xs text-muted-foreground">
                  <span>{d.paradas} parada(s) · {num(d.porciones, 0)} raciones</span>
                  {d.guia_estado && <span className={GUIA_CLS[d.guia_estado] ?? ''}>guía: {d.guia_estado}</span>}
                </span>
              </button>
            ))}
            {!despachos.length && <p className="text-sm text-muted-foreground">Sin despachos este día.</p>}
          </CardContent>
        </Card>

        {/* Detalle del despacho */}
        <Card className="lg:col-span-3">
          <CardHeader className="pb-2"><CardTitle className="text-base">Hoja de carga</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            {!det ? <p className="text-sm text-muted-foreground">Selecciona un despacho.</p> : (
              <>
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div>
                    <p className="font-medium">{det.codigo} — {det.ruta}</p>
                    <p className="text-xs text-muted-foreground">
                      {det.vehiculo || 'sin vehículo'}{det.placa ? ` · ${det.placa}` : ''}{det.chofer ? ` · ${det.chofer}` : ''}
                      {' · '}{num(det.total_porciones, 0)} raciones{det.total_contenedores > 0 ? ` · ${det.total_contenedores} contenedores` : ''}
                    </p>
                  </div>
                  <span className={`rounded-full px-2 py-0.5 text-xs ${ESTADO_CLS[det.estado]}`}>{det.estado.replace('_', ' ')}</span>
                </div>

                {/* Paradas con líneas */}
                <div className="space-y-3">
                  {det.paradas.map((par) => (
                    <div key={par.contrato_id} className="rounded-md border p-2">
                      <p className="text-sm font-medium">[{par.orden}] {par.cliente} <span className="text-xs text-muted-foreground">· {par.direccion || 'sin dirección'}{par.hora_objetivo ? ` · ${par.hora_objetivo.slice(0, 5)}` : ''}</span></p>
                      <table className="w-full text-sm">
                        <thead><tr className="border-b text-left text-xs text-muted-foreground">
                          <th className="py-1 pr-2">Servicio</th><th className="py-1 pr-2 text-right">Raciones</th>
                          <th className="py-1 pr-2 text-right">Contenedores</th><th className="py-1 pr-2">Menaje</th>
                        </tr></thead>
                        <tbody>
                          {par.lineas.map((l) => (
                            <tr key={l.id} className="border-b last:border-0">
                              <td className="py-1 pr-2">{SERVICIO_LABEL[l.servicio] ?? l.servicio}</td>
                              <td className="py-1 pr-2 text-right font-mono">{num(l.porciones, 0)}</td>
                              <td className="py-1 pr-2 text-right">
                                {det.estado === 'borrador' ? (
                                  <input className={`${fieldCls} w-20 text-right`} type="number"
                                    placeholder={sugerir(l.porciones)}
                                    value={conts[l.id] ?? (l.contenedores || '')}
                                    onChange={(e) => setConts((s) => ({ ...s, [l.id]: e.target.value }))} />
                                ) : <span className="font-mono">{l.contenedores}</span>}
                              </td>
                              <td className="py-1 pr-2">
                                {det.estado === 'borrador' ? (
                                  <input className={`${fieldCls} w-32`} placeholder="Termo 40L…"
                                    value={menajes[l.id] ?? (l.menaje ?? '')}
                                    onChange={(e) => setMenajes((s) => ({ ...s, [l.id]: e.target.value }))} />
                                ) : <span className="text-xs">{l.menaje || '—'}</span>}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ))}
                </div>

                {/* Checklist de salida */}
                {det.estado === 'borrador' && (
                  <div className="rounded-md border border-amber-400 bg-amber-50/50 p-3 dark:bg-amber-900/10">
                    <p className="flex items-center gap-2 text-sm font-medium"><Thermometer className="h-4 w-4" /> Checklist de salida</p>
                    <p className="pb-2 text-xs text-muted-foreground">Nada sale sin temperatura de la carga y sin indicar en qué se transporta cada parada.</p>
                    <div className="flex flex-wrap items-end gap-2">
                      <div><label className="block text-xs text-muted-foreground">Temperatura de salida (°C)</label>
                        <input className={`${fieldCls} w-32`} type="number" step="0.5" placeholder="ej. 68" value={temp} onChange={(e) => setTemp(e.target.value)} /></div>
                      <Button disabled={cargar.isPending} onClick={() => cargar.mutate()}><PackageCheck className="mr-1 h-4 w-4" /> Registrar carga</Button>
                    </div>
                  </div>
                )}

                {det.estado !== 'borrador' && det.temp_salida_c != null && (
                  <p className="text-sm"><Thermometer className="mr-1 inline h-4 w-4 text-sky-600" /> Temperatura de salida: <strong>{det.temp_salida_c} °C</strong>{det.hora_salida ? ` · salió ${new Date(det.hora_salida).toLocaleTimeString(LOCALE, { hour: '2-digit', minute: '2-digit' })}` : ''}</p>
                )}

                {/* Guía + acciones */}
                <div className="flex flex-wrap items-center gap-2 border-t pt-3">
                  {(det.estado === 'cargado' || det.estado === 'en_ruta') && !det.guia?.id && (
                    <Button variant="outline" disabled={emitirGuia.isPending} onClick={() => emitirGuia.mutate()}><FileText className="mr-1 h-4 w-4" /> Emitir guía de remisión</Button>
                  )}
                  {det.guia?.id && (
                    <span className="text-sm">
                      Guía SRI: <strong>{det.guia.serie}</strong>
                      <span className={`ml-1 rounded-full bg-muted px-2 py-0.5 text-xs ${GUIA_CLS[det.guia.estado_emision] ?? ''}`}>{det.guia.estado_emision}</span>
                      {det.guia.estado_emision === 'error' && det.guia.mensaje_sri && <span className="ml-2 text-xs text-red-600">{det.guia.mensaje_sri}</span>}
                      {(det.guia.estado_emision === 'error' || det.guia.estado_emision === 'rechazado' || det.guia.estado_emision === 'devuelto') && (
                        <button className="ml-2 text-primary underline" onClick={() => emitirGuia.mutate()}>reintentar</button>
                      )}
                    </span>
                  )}
                  {det.estado === 'cargado' && (
                    <Button size="sm" onClick={() => estado.mutate('en_ruta')}><Send className="mr-1 h-3.5 w-3.5" /> Poner en ruta</Button>
                  )}
                  {det.estado !== 'anulado' && det.estado !== 'cerrado' && (
                    <Button size="sm" variant="ghost" className="text-red-600" onClick={async () => { if (await dlg.confirmar('¿Anular este despacho?', { peligro: true })) estado.mutate('anulado'); }}>Anular</Button>
                  )}
                  <button className="ml-auto text-xs text-primary underline" onClick={() => openTab({ id: '/catering/rutas', title: 'Rutas de distribución', path: '/catering/rutas' })}>gestionar rutas</button>
                </div>

                {/* Distribución y entregas (POD) */}
                {enReparto && (
                  <div className="space-y-3 border-t pt-3">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <p className="flex items-center gap-2 text-sm font-medium"><MapPin className="h-4 w-4" /> Distribución y entregas</p>
                      <Button size="sm" variant="outline" onClick={() => genToken.mutate(det.ruta_id)}><QrCode className="mr-1 h-3.5 w-3.5" /> Enlace del repartidor</Button>
                    </div>

                    {repartoUrl && (
                      <div className="flex flex-col items-center gap-2 rounded-md border bg-muted/30 p-3 sm:flex-row sm:items-start">
                        <div className="rounded bg-white p-2"><QRCodeSVG value={repartoUrl} size={104} /></div>
                        <div className="min-w-0 flex-1 text-xs">
                          <p className="text-muted-foreground">El repartidor abre este enlace en su celular (sin usuario ni contraseña) y registra cada entrega. El QR es permanente para esta ruta.</p>
                          <div className="mt-1 flex items-center gap-2">
                            <code className="min-w-0 flex-1 truncate rounded bg-background px-2 py-1">{repartoUrl}</code>
                            <button className="shrink-0 text-primary" onClick={() => { void navigator.clipboard?.writeText(repartoUrl); toast.success('Enlace copiado'); }}><Copy className="h-4 w-4" /></button>
                          </div>
                        </div>
                      </div>
                    )}

                    <table className="w-full text-sm">
                      <thead><tr className="border-b text-left text-xs text-muted-foreground">
                        <th className="py-1 pr-2">#</th><th className="py-1 pr-2">Cliente</th>
                        <th className="py-1 pr-2">Estado</th><th className="py-1 pr-2">Recibió</th><th className="py-1 pr-2 text-right">°C</th><th />
                      </tr></thead>
                      <tbody>
                        {entregas.map((e) => (
                          <tr key={e.contrato_id} className="border-b last:border-0">
                            <td className="py-1.5 pr-2 font-mono">{e.orden}</td>
                            <td className="py-1.5 pr-2">{e.cliente}<span className="block text-xs text-muted-foreground">{num(e.raciones, 0)} raciones</span></td>
                            <td className="py-1.5 pr-2">
                              {e.estado === 'entregado' ? <span className="inline-flex items-center gap-1 text-green-700 dark:text-green-400"><CheckCircle2 className="h-4 w-4" /> entregado</span>
                                : e.estado === 'incidencia' ? <span className="inline-flex items-center gap-1 text-amber-600"><AlertTriangle className="h-4 w-4" /> incidencia</span>
                                : <span className="text-muted-foreground">pendiente</span>}
                              {e.incidencia_motivo && <span className="block text-xs text-amber-600">{e.incidencia_motivo}</span>}
                            </td>
                            <td className="py-1.5 pr-2 text-xs">{e.receptor_nombre ?? '—'}
                              {(e.tiene_firma || e.tiene_foto || e.origen) && (
                                <span className="block text-muted-foreground">
                                  {e.firma_url ? <a href={e.firma_url} target="_blank" rel="noreferrer" className="text-primary underline">firma</a> : e.tiene_firma ? 'firma' : ''}
                                  {(e.tiene_firma && (e.tiene_foto || e.origen)) ? ' · ' : ''}
                                  {e.foto_url ? <a href={e.foto_url} target="_blank" rel="noreferrer" className="text-primary underline">foto</a> : e.tiene_foto ? 'foto' : ''}
                                  {(e.tiene_foto && e.origen) ? ' · ' : ''}
                                  {e.origen === 'oficina' ? 'oficina' : ''}
                                  {e.media_estado === 'pendiente' || e.media_estado === 'procesando' ? ' · media…' : ''}
                                </span>
                              )}
                            </td>
                            <td className="py-1.5 pr-2 text-right font-mono">{e.temp_llegada_c ?? '—'}</td>
                            <td className="py-1.5 text-right">
                              {e.estado === 'pendiente' && det.estado !== 'cerrado' && (
                                <button className="text-xs text-primary underline" onClick={() => registrarEntrega.mutate({ contrato_id: e.contrato_id, estado: 'entregado' })}>marcar entregado</button>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    <p className="text-xs text-muted-foreground">{entregas.filter((e) => e.estado !== 'pendiente').length}/{entregas.length} paradas registradas · el repartidor las marca desde su celular; aquí puedes registrarlas de respaldo.</p>

                    <MenajeRetorno despachoId={det.id} estado={det.estado}
                      paradas={det.paradas.map((par) => ({ contrato_id: par.contrato_id, cliente: par.cliente, lineas: par.lineas.map((l) => ({ id: l.id, servicio: l.servicio, porciones: l.porciones })) }))}
                      onCerrado={() => invalidar()} />
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
